import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { LexicalTypeaheadMenuPlugin, MenuOption, MenuResolution, useBasicTypeaheadTriggerMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
    $createTextNode,
    $getEditor,
    $getSelection,
    $insertNodes,
    $isRangeSelection,
    $setSelection,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_NORMAL,
    createCommand,
    LexicalNode,
    TextNode,
} from 'lexical'
import { RefObject, useEffect, useState } from 'react'

import { createMatchSorter } from '~/utils/sort'

import { useMentions } from '../../useMentions'
import { $createMentionNode } from './MentionNode'
import { MentionsMenu } from './MentionsMenu'
import type { MentionItem } from './types'

type MentionsPluginProps = {
    rootPortalRef?: RefObject<HTMLDivElement | null>
}

export class MentionOption extends MenuOption {
    mention: MentionItem

    constructor(mention: MentionItem) {
        super(`${mention.type}-${mention.id.toString()}`)
        this.mention = mention
    }
}

export const MENTIONS_TRIGGER_CHAR = '@'

export const OPEN_MENTIONS_MENU_COMMAND = createCommand<undefined>('OPEN_MENTIONS_MENU_COMMAND')

const $openMentionsMenuCommand = () => {
    const selection = $getSelection()
    const editor = $getEditor()

    if (!$isRangeSelection(selection)) {
        editor.focus()
        return false
    }

    const charBeforeCursor = selection.anchor
        .getNode()
        .getTextContent()
        .charAt(selection.anchor.offset - 1)
    // dont add more chars if the char before is the trigger char, menu should already be open
    if (charBeforeCursor === MENTIONS_TRIGGER_CHAR) {
        editor.focus()
        return false
    }
    const nodesToInsert: LexicalNode[] = []
    // add empty space if the char before is not whitespace or empty string
    if (!new RegExp(/(\s|^$)/).test(charBeforeCursor)) {
        nodesToInsert.push($createTextNode(' '))
    }
    nodesToInsert.push($createTextNode(MENTIONS_TRIGGER_CHAR))
    $insertNodes(nodesToInsert)
    return false
}

export function MentionsPlugin({ rootPortalRef }: MentionsPluginProps) {
    const [editor] = useLexicalComposerContext()
    const [query, setQuery] = useState<string | null>(null)

    // needed to be able to set the position of the menu properly
    const [resolution, setResolution] = useState<MenuResolution | null>(null)

    const triggerMatch = useBasicTypeaheadTriggerMatch(MENTIONS_TRIGGER_CHAR, {
        minLength: 0,
        allowWhitespace: true,
    })

    const mentionGroups = useMentions(query)
    const groupsWithMatches = mentionGroups.reduce((acc, group) => (group.options.length > 0 ? acc + 1 : acc), 0)

    const optionGroups = mentionGroups
        .map(group => ({
            entityType: group.entityType,
            label: group.label,
            options: group.options
                .slice(0, groupsWithMatches === 1 ? 10 : 3)
                .sort(createMatchSorter(query, option => option.value))
                .map(option => new MentionOption(option)),
        }))
        .filter(group => group.options.length > 0)

    const flattenedOptions = optionGroups.flatMap(group => group.options)

    const onSelectOption = (selectedOption: MentionOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
        editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) {
                return
            }

            if (nodeToRemove) {
                nodeToRemove.remove()
            }
            selection.insertNodes([$createMentionNode(selectedOption.mention)])
            selection.insertText(' ')
        })
        closeMenu()
    }

    useEffect(() => editor.registerCommand(OPEN_MENTIONS_MENU_COMMAND, $openMentionsMenuCommand, COMMAND_PRIORITY_NORMAL), [editor])

    const closeMenu = () =>
        editor.update(() => {
            const selection = $getSelection()
            if (!$isRangeSelection(selection)) {
                return
            }
            // no good way to programmatically close the menu
            // but clearing the selection will close it
            // see https://github.com/facebook/lexical/blob/a64fd2e5e5c5b590a7dc9699e7a3d91f8770c2b7/packages/lexical-react/src/LexicalTypeaheadMenuPlugin.tsx#L271
            $setSelection(null)
        })

    return (
        <LexicalTypeaheadMenuPlugin<MentionOption>
            onQueryChange={query => setQuery(query)}
            onSelectOption={onSelectOption}
            triggerFn={triggerMatch}
            options={flattenedOptions}
            onOpen={r => {
                setResolution(r)
            }}
            preselectFirstItem
            commandPriority={COMMAND_PRIORITY_HIGH}
            menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
                if (!anchorElementRef.current || !resolution || flattenedOptions.length < 1) {
                    return null
                }

                return (
                    <MentionsMenu
                        onOutsideClick={closeMenu}
                        resolution={resolution}
                        anchorElementRef={anchorElementRef as RefObject<HTMLElement>}
                        groupedOptions={optionGroups}
                        selectedOption={selectedIndex !== null ? flattenedOptions[selectedIndex] : null}
                        onSelect={option => {
                            setHighlightedIndex(flattenedOptions.indexOf(option))
                            selectOptionAndCleanUp(option)
                        }}
                        setHighlightedOption={option => setHighlightedIndex(flattenedOptions.indexOf(option))}
                        rootPortalRef={rootPortalRef}
                    />
                )
            }}
        />
    )
}
